import type { IconName } from '@/shared/ui/Icon';

/**
 * Mapa de navegacao da aplicacao.
 *
 * A estrutura vem do levantamento funcional registrado em docs/LEGACY_ANALYSIS.md
 * e permanece organizada nos mesmos seis grupos, que refletem como a operacao de
 * garantia funciona na pratica.
 *
 * Cada item declara a permissao e a funcionalidade que exige. O menu monta apenas
 * o que a empresa contratou e o usuario pode acessar. Isso e apresentacao: a
 * decisao de acesso continua sendo do servidor a cada requisicao.
 */

export interface NavItem {
  key: string;
  label: string;
  path: string;
  icon: IconName;
  permission: string;
  feature?: string;
  /** Fase do roteiro em que o modulo entra. Ver docs/MODULES.md. */
  phase: number;
  /**
   * Publico do item. `broker` aparece so para quem tem escopo de corretora, e
   * `insurer` so para quem nao tem. Sem publico, o item vale para os dois. A
   * permissao continua valendo nos tres casos: o publico separa a tela da corretora
   * da tela da seguradora quando as duas pedem a mesma permissao.
   */
  audience?: 'insurer' | 'broker';
}

/** O que a sessao concede, para decidir quais itens o menu mostra. */
export interface NavAccess {
  can: (permission: string) => boolean;
  hasFeature: (feature: string) => boolean;
  brokerScoped: boolean;
}

/** Informa se o item aparece no menu da sessao. */
export function isNavItemVisible(item: NavItem, access: NavAccess): boolean {
  if (item.audience === 'broker' && !access.brokerScoped) return false;
  if (item.audience === 'insurer' && access.brokerScoped) return false;
  return (
    access.can(item.permission) && (!item.feature || access.hasFeature(item.feature))
  );
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const navigation: NavGroup[] = [
  {
    label: 'Visao geral',
    items: [
      {
        key: 'dashboard',
        label: 'Painel',
        path: '/',
        icon: 'dashboard',
        permission: 'dashboard.painel.view',
        feature: 'dashboard.executivo',
        phase: 6,
      },
      {
        // Relatorios (P-BI-1): o BI do OnPoint ("BI Integrado", so do ADMIN la),
        // construido no SeguraCore. O Painel continua como visao rapida.
        key: 'relatorios',
        label: 'Relatorios',
        path: '/bi',
        icon: 'chart',
        permission: 'bi.painel.view',
        feature: 'bi.relatorios',
        phase: 6,
      },
      {
        key: 'operacoes',
        label: 'Operacoes',
        path: '/operacoes',
        icon: 'target',
        permission: 'operacoes.operacao.view',
        feature: 'operacoes.esteira',
        phase: 3,
      },
      {
        key: 'demandas',
        label: 'Demandas e SLA',
        path: '/demandas',
        icon: 'clock',
        permission: 'demandas.demanda.view',
        feature: 'operacoes.esteira',
        phase: 6,
      },
    ],
  },
  {
    label: 'Relacionamento',
    items: [
      {
        key: 'corretores',
        label: 'Corretores',
        path: '/corretores',
        icon: 'users',
        permission: 'corretores.corretor.view',
        feature: 'corretores.gestao',
        phase: 3,
        audience: 'insurer',
      },
      {
        // A corretora ve so o proprio cadastro. A lista e a esteira de ativacao sao
        // ferramentas da seguradora.
        key: 'dados-corretora',
        label: 'Dados da corretora',
        path: '/corretora',
        icon: 'bank',
        permission: 'corretores.corretor.view',
        feature: 'corretores.gestao',
        phase: 3,
        audience: 'broker',
      },
      {
        // Assessorias (P-COR-3): a funcionalidade nasce desligada em toda empresa,
        // como na Sudaseg (CFG-003), e o item so aparece com ela ligada.
        key: 'assessorias',
        label: 'Assessorias',
        path: '/assessorias',
        icon: 'users',
        permission: 'corretores.assessoria.view',
        feature: 'corretores.assessoria',
        phase: 3,
        audience: 'insurer',
      },
      {
        key: 'tomadores',
        label: 'Tomadores',
        path: '/tomadores',
        icon: 'briefcase',
        permission: 'tomadores.tomador.view',
        feature: 'tomadores.gestao',
        phase: 3,
      },
      {
        // Segurado e beneficiario (P-SEG-1). No OnPoint so o ADMIN ve; a corretora
        // nao tem a permissao no escopo dela, e o item fica so para a seguradora.
        key: 'segurados',
        label: 'Segurados',
        path: '/segurados',
        icon: 'shield',
        permission: 'segurados.segurado.view',
        feature: 'tomadores.gestao',
        phase: 3,
        audience: 'insurer',
      },
    ],
  },
  {
    label: 'Esteira de operacoes',
    items: [
      {
        // Esteiras do OnPoint (P-EST-1): o quadro em colunas de cada esteira, sobre a
        // maquina de estados da operacao. As filas por etapa continuam abaixo.
        key: 'esteiras',
        label: 'Esteiras',
        path: '/esteiras',
        icon: 'layers',
        permission: 'esteiras.esteira.view',
        feature: 'operacoes.esteira',
        phase: 4,
        audience: 'insurer',
      },
      {
        key: 'credito',
        label: 'Analise de credito',
        path: '/credito',
        icon: 'coins',
        permission: 'credito.analise.view',
        feature: 'credito.analise',
        phase: 4,
      },
      {
        key: 'subscricao',
        label: 'Subscricao',
        path: '/subscricao',
        icon: 'shield',
        permission: 'subscricao.analise.view',
        feature: 'subscricao.analise',
        phase: 4,
      },
      {
        key: 'juridico',
        label: 'Juridico',
        path: '/juridico',
        icon: 'gavel',
        permission: 'juridico.parecer.view',
        feature: 'juridico.parecer',
        phase: 4,
      },
      {
        key: 'comercial',
        label: 'Comercial e cotacoes',
        path: '/comercial',
        icon: 'target',
        // A tela le a fila de operacoes, e e essa a permissao que o servidor
        // exige. comercial.proposta.view existe no catalogo e nenhuma rota a
        // consulta: conferir por ela aqui escondia a tela de quem podia abri-la
        // e a oferecia a quem receberia uma recusa.
        permission: 'operacoes.operacao.view',
        feature: 'comercial.cotacao',
        phase: 4,
        // A fila e da seguradora: a corretora acompanha as cotacoes dela em
        // Operacoes (P-NCT; defeito 3 de "Operacao e esteira" do 47, anexo C).
        audience: 'insurer',
      },
      {
        key: 'minutas',
        label: 'Minutas',
        path: '/minutas',
        icon: 'file',
        permission: 'minutas.minuta.view',
        feature: 'minutas.geracao',
        phase: 5,
      },
      {
        key: 'ccg',
        label: 'CCG',
        path: '/ccg',
        icon: 'stamp',
        permission: 'ccg.carta.view',
        feature: 'ccg.geracao',
        phase: 5,
      },
      {
        key: 'assinaturas',
        label: 'Assinaturas',
        path: '/assinaturas',
        icon: 'signature',
        permission: 'assinaturas.fluxo.view',
        feature: 'assinaturas.eletronica',
        phase: 5,
      },
      {
        key: 'emissao',
        label: 'Emissao',
        path: '/emissao',
        icon: 'send',
        // Mesma fila de operacoes, filtrada por etapa. A emissao em si tem
        // permissao propria, exigida no ato de emitir.
        permission: 'operacoes.operacao.view',
        feature: 'emissao.apolice',
        phase: 5,
        // A fila e da seguradora; a corretora pede a emissao pela cotacao (P-NCT).
        audience: 'insurer',
      },
    ],
  },
  {
    label: 'Carteira',
    items: [
      {
        key: 'apolices',
        label: 'Apolices',
        path: '/apolices',
        icon: 'file',
        permission: 'apolices.apolice.view',
        feature: 'carteira.apolices',
        phase: 5,
      },
      {
        key: 'fiancas',
        label: 'Fiancas bancarias',
        path: '/fiancas',
        icon: 'bank',
        // Fianca e apolice na mesma tabela: seguradora emite seguro garantia,
        // banco emite fianca bancaria. A carteira de fiancas pede a permissao e a
        // funcionalidade da fianca, no menu e na API (P-APO-1; antes a API pedia a
        // da apolice, inventario 47, secao 3.6).
        permission: 'fiancas.fianca.view',
        feature: 'carteira.fiancas',
        phase: 5,
      },
      {
        key: 'endossos',
        label: 'Endossos',
        path: '/endossos',
        icon: 'copy',
        permission: 'endossos.endosso.view',
        feature: 'carteira.endossos',
        phase: 5,
      },
      {
        key: 'renovacoes',
        label: 'Renovacoes',
        path: '/renovacoes',
        icon: 'refresh',
        permission: 'renovacoes.renovacao.view',
        feature: 'carteira.renovacoes',
        phase: 5,
      },
      {
        key: 'cancelamentos',
        label: 'Cancelamentos',
        path: '/cancelamentos',
        icon: 'close',
        permission: 'cancelamentos.cancelamento.view',
        feature: 'carteira.cancelamentos',
        phase: 5,
      },
      {
        key: 'sinistros',
        label: 'Sinistros',
        path: '/sinistros',
        icon: 'alert',
        permission: 'sinistros.sinistro.view',
        feature: 'sinistros.gestao',
        phase: 6,
      },
    ],
  },
  {
    label: 'Financeiro',
    items: [
      {
        key: 'comissoes',
        label: 'Comissoes',
        path: '/comissoes',
        icon: 'coins',
        permission: 'comissoes.comissao.view',
        feature: 'financeiro.comissoes',
        phase: 6,
      },
      {
        key: 'taxas',
        label: 'Tabela de taxas',
        path: '/taxas',
        icon: 'percent',
        permission: 'taxas.tabela.view',
        feature: 'financeiro.precificacao',
        phase: 6,
      },
      {
        key: 'agravos',
        label: 'Agravos e descontos',
        path: '/agravos',
        icon: 'percent',
        permission: 'agravos.fator.view',
        feature: 'financeiro.precificacao',
        phase: 6,
      },
    ],
  },
  {
    label: 'Administracao',
    items: [
      {
        key: 'seguradoras',
        label: 'Seguradoras',
        path: '/seguradoras',
        icon: 'shield',
        permission: 'seguradoras.seguradora.view',
        feature: 'cadastros.contrapartes',
        phase: 3,
      },
      {
        key: 'bancos',
        label: 'Bancos',
        path: '/bancos',
        icon: 'bank',
        permission: 'bancos.banco.view',
        feature: 'cadastros.contrapartes',
        phase: 3,
      },
      {
        // Parametrizacao do OnPoint (P-PAR-1): o menu de cartoes e a lista de
        // modalidades, que substitui "Produtos e modalidades" (/produtos continua
        // valendo e leva a ela).
        key: 'parametrizacao',
        label: 'Parametrizacao',
        path: '/parametrizacao',
        icon: 'settings',
        permission: 'parametrizacao.modalidade.view',
        feature: 'cadastros.produtos',
        phase: 3,
        audience: 'insurer',
      },
      {
        key: 'produtos',
        label: 'Produtos e modalidades',
        path: '/parametrizacao/modalidades',
        icon: 'layers',
        permission: 'parametrizacao.modalidade.view',
        feature: 'cadastros.produtos',
        phase: 3,
        audience: 'insurer',
      },
      {
        // Endosso de aniversario e renovacao da parametrizacao (P-PAR-3). No OnPoint
        // as telas abrem so por endereco; aqui ficam no menu e nos cartoes.
        key: 'parametrizacao-endosso',
        label: 'Endosso de aniversario',
        path: '/parametrizacao/endosso',
        icon: 'file',
        permission: 'parametrizacao.aniversario.view',
        feature: 'cadastros.produtos',
        phase: 3,
        audience: 'insurer',
      },
      {
        key: 'parametrizacao-renovacao',
        label: 'Renovacao de apolices',
        path: '/parametrizacao/renovacao',
        icon: 'refresh',
        permission: 'parametrizacao.renovacao.view',
        feature: 'cadastros.produtos',
        phase: 3,
        audience: 'insurer',
      },
      {
        key: 'documentos',
        label: 'Documentos',
        path: '/documentos',
        icon: 'file',
        // A primeira aba e a dos documentos da seguradora, que a corretora tambem le
        // (P-DOC). Quem le o repositorio recebeu a leitura nova na migration.
        permission: 'documentos.seguradora.view',
        feature: 'documentos.repositorio',
        phase: 6,
      },
      {
        key: 'templates',
        label: 'Templates',
        path: '/templates',
        icon: 'copy',
        permission: 'templates.template.view',
        feature: 'minutas.geracao',
        phase: 6,
      },
      {
        key: 'usuarios',
        label: 'Usuarios e perfis',
        path: '/usuarios',
        icon: 'lock',
        permission: 'usuarios.papel.view',
        feature: 'administracao.usuarios',
        phase: 3,
        audience: 'insurer',
      },
      {
        // Quem administra a corretora cuida das pessoas dela, sem a matriz de
        // perfis da empresa.
        key: 'usuarios-corretora',
        label: 'Usuarios da corretora',
        path: '/corretora/usuarios',
        icon: 'lock',
        permission: 'usuarios.usuario.view',
        feature: 'administracao.usuarios',
        phase: 3,
        audience: 'broker',
      },
      {
        // Termos de aceite do OnPoint (P-USU-2): quem aceitou o termo de uso e as
        // declaracoes da proposta. A seguradora ve todos; a corretora, os dela.
        key: 'termos-aceite',
        label: 'Termos de aceite',
        path: '/termos-aceite',
        icon: 'file',
        permission: 'termos.aceite.view',
        phase: 3,
      },
      {
        key: 'integracoes',
        label: 'Integracoes',
        path: '/integracoes',
        icon: 'plug',
        permission: 'integracoes.integracao.view',
        feature: 'administracao.integracoes',
        phase: 7,
      },
      {
        // Gerenciamento de consultas a bureau do OnPoint (P-INT).
        key: 'consultas-bureau',
        label: 'Consultas a bureau',
        path: '/consultas-bureau',
        icon: 'target',
        permission: 'integracoes.erro_consulta.view',
        feature: 'administracao.integracoes',
        phase: 7,
        audience: 'insurer',
      },
      {
        // Gerenciamento de logs do OnPoint (P-LOG): a trilha, os acessos e as
        // rotinas. Os logs sao da seguradora; a corretora nao ve o item.
        key: 'logs',
        label: 'Logs',
        path: '/logs',
        icon: 'clock',
        permission: 'auditoria.log.view',
        feature: 'administracao.auditoria',
        phase: 3,
        audience: 'insurer',
      },
      {
        key: 'configuracoes',
        label: 'Configuracoes',
        path: '/configuracoes',
        icon: 'settings',
        permission: 'configuracoes.tenant.view',
        phase: 3,
      },
    ],
  },
];

/** Devolve todos os itens em lista unica, util para roteamento. */
export function allNavItems(): NavItem[] {
  return navigation.flatMap((group) => group.items);
}
